import { Component, OnInit, OnDestroy, Input} from '@angular/core';
import { Subscription, Observable, of, forkJoin, from } from "rxjs";
import { tap, mergeMap, map, switchMap } from "rxjs/operators";
//import { GridOptions, ColDef, GridApi, ColumnApi, RedrawRowsParams } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { ComplianceReport, ComplianceReportSearchSpec, ComplianceReportStatus, ComplianceReportingType } from "../../../../issues/models/compliance.model";
import { IssuesService } from "../../../../common/services/issues.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { Organization, OrganizationStub } from "../../../../common/models/organization.model";
import { OrganizationService } from "../../../../common/services/organization.service";
import { TabComponent } from '../../../../common/components/tabs/tab.component';
import { RouterLinkRendererComponent } from "../../../../common/components/ag-grid/router-link-renderer.component";
import { PendingFlow } from '../../../../flow/models/flow.model';
import { catchError } from 'rxjs/operators';
import { FlowIndex } from '../../../../common/models/flow.dto';
import { ApplicationState, ApplicationType, MetadataSearchSpec } from '../../../../applications/models/applications.models';
import { ApplicationsService } from '../../../../applications/services/applications.service';
import { GroupSearchSpec, GroupService } from '../../../../admin/services/group.service';
import { Group, GroupStub } from '../../../../admin/models/group.model';
import { ContactStub } from '../../../../admin/models/contact.model';
import { PendingWorkOrderAssignment } from '../../../../common/models/work-order.model';
import { WorkOrderService } from '../../../../common/services/work-order.service';
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from '../../../../common/components/ag-grid/filters/boolean-filter.component';
import { CheckboxRendererComponent } from "../../../../common/components/ag-grid/checkbox-renderer.component";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';


@Component({
    selector: 'compliance-reports-internal-view',
    templateUrl: './compliance-reports-internal-view.component.html',
    styleUrls: ['./compliance-reports-internal-view.component.scss'],
    standalone: false
})
export class ComplianceReportsInternalViewComponent implements OnInit, OnDestroy {
    @Input() widgetTitle: string;


    incompleteReports: PendingFlow[];
    approvedReports: PendingFlow[];
    returnedReports: PendingFlow[];
    pendingReports: PendingFlow[];
    organizations: OrganizationStub[];
    types: ComplianceReportingType[] = [];
    allUsers: ContactStub[] = [];
    groups: Group[] = [];


	approvedGridOptions: GridOptions;
	approvedGridApi?: GridApi;
	returnedGridOptions: GridOptions;
	returnedGridApi?: GridApi;
	pendingGridOptions: GridOptions;
	pendingGridApi?: GridApi;
	incompleteGridOptions: GridOptions;
	incompleteGridApi?: GridApi;

    apiNumberColumn: ColDef = {
        headerName: "API #",
        headerTooltip: "API #",
        field: "aPINumber",
        tooltipField: "aPINumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    applicationNumberColumn: ColDef = {
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: "applicationNumber",
            tooltipField: "applicationNumber",
            openInNewWindow: false,
            url: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/review` }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150


    } as ColDef;

    incompleteApplicationNumber: ColDef = {
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: "applicationNumber",
            tooltipField: "applicationNumber",
            openInNewWindow: false,
            url: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/resume` }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    approvedDateColumn: ColDef = {
        comparator: dateComparator,
        headerName: "Approved Date",
        headerTooltip: "Approved Date",
        field: "approvedDate",
        tooltipField: "approvedDate",
        valueGetter: this.formatter.formatDate("approvedDate"),
        tooltipValueGetter: this.formatter.formatDate("approvedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 150
    } as ColDef;


    approvedApplicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 160
    } as ColDef;


    pendingApplicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: "applicationNumber",
            tooltipField: "applicationNumber",
            openInNewWindow: false,
            url: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/resume` }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    returnedApplicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: "applicationNumber",
            tooltipField: "applicationNumber",
            openInNewWindow: false,
            url: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/review` }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    organizationColumn: ColDef = {
        headerName: "Organization",
        headerTooltip: "Organization",
        field: "companyName",
        valueFormatter: this.formatOrganization("companyName"),
        tooltipValueGetter: this.formatOrganization("companyName"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    reportTypeColumn: ColDef = {
        headerName: "Type of Report",
        headerTooltip: "Type of Report",
        field: "reportType",
        valueGetter: this.formatReportType("reportType"),
        tooltipValueGetter: this.formatReportType("reportType"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    submittedColumn: ColDef = {
        comparator: dateComparator,
        headerName: "Submitted Date",
        headerTooltip: "Submitted Date",
        field: "createdDate",
        tooltipField: "createdDate",
        valueGetter: this.formatter.formatDate("createdDate"),
        tooltipValueGetter: this.formatter.formatDate("createdDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 150

    } as ColDef;

    wellNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Name",
        headerTooltip: "Well Name",
        field: "wellName",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        valueFormatter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        minWidth: 150

    } as ColDef;

    wellNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Number",
        headerTooltip: "Well Number",
        field: "wellNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    dueDateColumn: ColDef = {
        comparator: dateComparator,
        headerClass: "wrapped",
        headerName: "Due Date",
        headerTooltip: "Due Date",
        field: "dueDate",
        tooltipField: "dueDate",
        valueGetter: this.formatter.formatDate("dueDate"),
        tooltipValueGetter: this.formatter.formatDate("dueDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 150
    } as ColDef;

    sourceColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Source",
        headerTooltip: "Source",
        field: "onBehalf",
        filter: AgGridBooleanFilterComponent,
        valueFormatter: this.formatter.formatSourceBoolean("onBehalf"),
        tooltipValueGetter: this.formatter.formatSourceBoolean("onBehalf"),
        filterParams: {
            isInServerSideFilterMode: false,
            trueValueDisplayFormat: "Paper",
            falseValueDisplayFormat: "RICS"
        } as IBooleanFilterParams,
        minWidth: 150,
        cellRenderer: CheckboxRendererComponent,
        cellRendererParams: {
            action: () => { return false; },
            field: "onBehalf",
            description: "Source",
            isVisible: () => { return true; },
            isEnabled: () => { return false; },
            renderAsYesNo: true,
            textRenderOptions: {
                trueOption: "Paper",
                falseOption: "RICS"
            }
        }
    }



    subscription: Subscription = new Subscription();

    constructor(private issuesService: IssuesService, private formatter: GridFormatterService, private organizationService: OrganizationService, public appService: ApplicationsService,
        public groupService: GroupService, public workOrderService: WorkOrderService) {
    }

    ngOnInit() {
       
        let type$: Observable<ComplianceReportingType[]> = (this.types == null || this.types.length == 0) ?
            this.issuesService.getComplianceReportingTypes().pipe(tap((types: ComplianceReportingType[]) => { this.types = types; })) : of(this.types);

        let tSubs$ = type$.subscribe();
        this.subscription.add(tSubs$);

        this.loadData();
        this.pendingGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildPendingColumns(), false);
        this.pendingGridOptions.headerHeight = 50;
		this.pendingGridOptions.context = {
			componentParent: this
		};
		this.pendingGridOptions.onGridReady = (params) => { this.pendingGridApi = params.api; };
        this.returnedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildReturnedColumns(), false);
		this.returnedGridOptions.headerHeight = 50;
		this.returnedGridOptions.onGridReady = (params) => { this.returnedGridApi = params.api; };
        this.approvedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildApprovedColumn(), false);
        this.approvedGridOptions.headerHeight = 50;
		this.approvedGridOptions.onGridReady = (params) => { this.approvedGridApi = params.api; };
        this.incompleteGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildIncompleteColumns(), false);
        this.incompleteGridOptions.headerHeight = 50;
		this.incompleteGridOptions.onGridReady = (params) => { this.incompleteGridApi = params.api; };

    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    formatOrganization(field: string): any {
        return (obj: any) => {
            let returnValue = "N/A";
            if (obj && obj.data && obj.data[field] != null) {
                returnValue = obj.data[field];
                if (this.organizations != null && this.organizations.length > 0) {
                    let orgs = this.organizations.filter(o => o.id == returnValue);
                    if (orgs == null || orgs.length === 0) orgs = this.organizations.filter(o => o.historicalOwnerNumber === returnValue);
                    if (orgs != null) {
                        const registered = orgs.filter(o => o.hasRegistered === true);
                        if (registered != null && registered.length > 0) {
                            returnValue = registered[0].name;
                        } else {
                            returnValue = orgs[0].name;
                        }
                    }
                }
            }
            return returnValue;
        }
    }

    formatReportType(field: string): any {
        return (obj: any) => {
            let returnValue = "N/A";
            if (obj && obj.data && obj.data[field] != null) {
                returnValue = obj.data[field];
                if (this.types != null) {
                    const type = this.types.find(o => o.typeId === returnValue);
                    if (type != null) returnValue = type.description;
                }
            }
            return returnValue;
        }
    }

    loadData(): void {
      
        var spec = {
            applicationTypes: [ApplicationType.ComplianceReporting],
            applicationStates: [],
        } as MetadataSearchSpec;

       
        let allUserSearchObs$ = this.groupService.get({ internal: true, stubs: false, fullContactDetails: true } as GroupSearchSpec) as Observable<Group[]>;

        let user$ = allUserSearchObs$.pipe(map((data: Group[]) => {
        
            
           //All Users
            this.groups = data as Group[];
            this.groups.forEach(g => this.allUsers = this.allUsers.concat(g.users))
            this.allUsers.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);

           

        }),
            switchMap(() => {

                let obs$ = this.appService.searchMetadata(spec).pipe(
                    map((meta: FlowIndex[]) => {
                        return meta
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of([]);
                    })
                )

                return obs$;


            })

        ).subscribe(
            {
                next: (meta: FlowIndex[]) => {
                    this.approvedReports = meta.filter(f => f.state == ApplicationState.Approved);
                    this.returnedReports = meta.filter(f => f.state == ApplicationState.Corrections)
                    this.pendingReports = meta.filter(f => f.state == ApplicationState.InitialReview)
                    this.incompleteReports = meta.filter(f => f.state == ApplicationState.Input && f.onBehalf == true);
                }, 
                error: (error) => {
                    console.error(error);
                }
            }
        );
        this.subscription.add(user$);
    }


    buildPendingColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.apiNumberColumn);
        colDefs.push(this.submittedColumn);
        colDefs.push(this.dueDateColumn);
        colDefs.push(this.applicationNumberColumn);
        colDefs.push(this.organizationColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.reportTypeColumn)
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            minWidth: 150,
            headerClass: "wrapped",
            cellEditorSelector: (params) => {
                let comp = {
                    component: "agRichSelectCellEditor",
                    params: {
                        formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                        values: []
                    }
                }
                let users = this.getFilteredUsers(params);
                comp.params.values = users;
                return comp;
            },
            cellEditorPopup: true,
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId) ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            maxWidth: 125,
            onCellValueChanged: ($event) => this.saveAssignments($event)
        } as ColDef);

        return colDefs;

    }

    buildReturnedColumns(): ColDef[] {
        let colDefs: ColDef[] = [];

        colDefs.push(this.submittedColumn);
        colDefs.push(this.dueDateColumn);
        colDefs.push(this.returnedApplicationNumberColumn);
        colDefs.push(this.organizationColumn);
        colDefs.push(this.apiNumberColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.reportTypeColumn)

        return colDefs;

    }

    buildIncompleteColumns(): ColDef[] {
        let colDefs: ColDef[] = [];

        colDefs.push(this.incompleteApplicationNumber);
        colDefs.push(this.organizationColumn);
        colDefs.push(this.apiNumberColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.reportTypeColumn)

        return colDefs;

    }

    buildApprovedColumn(): ColDef[] {
        let colDefs: ColDef[] = [];

        colDefs.push(this.submittedColumn);
        colDefs.push(this.dueDateColumn);
        colDefs.push(this.approvedApplicationNumberColumn);
        colDefs.push(this.organizationColumn);
        colDefs.push(this.apiNumberColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.reportTypeColumn)
        colDefs.push(this.sourceColumn);

        return colDefs;

    }


    buildUserList(groups: Group[]): ContactStub[] {
        let userList: ContactStub[] = [];
        groups.forEach(u => userList = userList.concat(u.users));
        userList = this.filterUniqueUsers(userList);
        userList = this.sortUsers(userList);
        userList.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);
        return userList;
    }

    sortUsers(users: ContactStub[]): ContactStub[] {
        return users.sort((a, b) => {
            if (a.lastName > b.lastName) return 1;
            if (a.lastName < b.lastName) return -1;
            if (a.lastName == b.lastName) {
                if (a.firstName > b.firstName) return 1;
                if (a.firstName < b.firstName) return -1;
            }
            return 0;
        })
    }

    getFilteredUsers(params: any): ContactStub[] {
        let values: ContactStub[] = [];
        if (params && params.data && params.data.assignedGroupId) {
            
            let group = this.groups.find(g => params.data.assignedGroupId == g.id);
            if (group != null) {

            values = group.users;
            }
        }
        return values;
    }


    filterUniqueUsers(users: ContactStub[]): ContactStub[] {
        return users.filter((user, i, arr) => arr.findIndex(t => t.id === user.id) === i);

    }

    getUserPromise(params: any): Promise<ContactStub[]> {
        return new Promise((resolve) => { setTimeout(() => resolve(this.getFilteredUsers(params)), 1000) });
    }

  
    renderUserDropdownOptions(params: any): string {
        let returnValue = params && params.value && params.value.displayName ? params.value.displayName : "Unassigned";
        return returnValue;
    }

    renderAssignedUser(params: any): string {
        let returnValue: string = "Unassigned";
        if (params && params.data && params.data.assignedUserId && params.data.assignedUserName) {
            returnValue = params.data.assignedUserName;
        }
        return returnValue;
    }

    assign(params: any): any {
        let result: boolean = false;
        if (params && params.newValue && params.newValue.id) {
            if (params.newValue.id != "-1") {
                params.data.assignedUserId = params.newValue.id;
            } else {
                params.data.assignedUserId = "";
            }

            if (params.context && params.context.componentParent) {
                let parent = <ComplianceReportsInternalViewComponent>params.context.componentParent;
                let selectedItem = parent.allUsers.find(u => u.id == params.newValue.id);
                if (selectedItem) {
                    params.data.assignedUserName = selectedItem.displayName;
                }
            }
            result = true;
        }
        return result;
    }


    cellValueChanged(params: any): void {
        if (params.api) {
            params.api.refreshCells();
        }
    }

    saveAssignments(event: any): void {
        let changedItem = <PendingFlow>event.data;
        if (event.context && event.context.componentParent) {
            const component = <ComplianceReportsInternalViewComponent>event.context.componentParent;
            const pending = new PendingWorkOrderAssignment(changedItem?.currentWorkItemId ?? "", changedItem.assignedUserId, changedItem.assignedUserName, true, changedItem.flowId);
            let updateSubs$ = component.workOrderService.assignAndReindexMetaData(pending).pipe()
                .subscribe({
                    next: (result) => { console.log("update managed"); },
                    error: (error) => { console.error(error); }
            });
            component.subscription.add(updateSubs$);
        }

    }


    onTabClicked(tab: TabComponent) {
        let api: GridApi | null = null;
        switch (tab.title) {
            case "Returned For Corrections":
				if (this.returnedGridApi != null) {
					api = this.returnedGridApi;
					this.returnedGridApi.setGridOption("columnDefs", this.buildReturnedColumns())
				}

                if (this.returnedReports == null) this.loadData();

                break;

            case "Accepted":
				if (this.approvedGridApi != null) {
					api = this.approvedGridApi;
					this.approvedGridApi.setGridOption("columnDefs", this.buildApprovedColumn())
				}
                if (this.approvedReports == null) this.loadData();

                break;
            case "Pending":

				if (this.pendingGridApi != null) {
					api = this.pendingGridApi;
					this.pendingGridApi.setGridOption("columnDefs", this.buildPendingColumns());
				}
                if (this.pendingReports == null) this.loadData();
                break;

            case "Incomplete":

				if (this.incompleteGridApi != null) {
					api = this.incompleteGridApi;
					this.incompleteGridApi.setGridOption("columnDefs", this.buildIncompleteColumns())
				}
                if (this.incompleteReports == null) this.loadData();
                break;
        }
        if (api != null) api.sizeColumnsToFit();


    }



}
